import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import AppContext from 'context/Context';
import config from 'util/config';
import logger from 'util/logger';
import './NotaryGptPage.css';

const LOAD_TIMEOUT_MS = 15000;
const DEFAULT_EMBED_SUBTEXT =
  'Ask me about Jurat Notary eJournal, notary laws, or general questions about notaries.  NotaryGPT does not look at or use your journal data.';

const normalizePortalUrl = url => {
  const trimmed = (url || '').trim();

  if (!trimmed) {
    return '';
  }

  return trimmed.replace(/\/+$/, '');
};

const getFirstValue = (source, keys) => {
  if (!source) {
    return '';
  }

  for (const key of keys) {
    const value = source[key];

    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }

  return '';
};

const resolveConversationUser = userMaster => ({
  userGuid: getFirstValue(userMaster, [
    'GUID',
    'Guid',
    'guid',
    'UserGUID',
    'UserGuid',
    'userGuid'
  ]),
  userEmail: getFirstValue(userMaster, [
    'Email',
    'email',
    'UserEmail',
    'userEmail'
  ]),
  userFirstName: getFirstValue(userMaster, [
    'FirstName',
    'firstName',
    'UserFirstName',
    'userFirstName'
  ]),
  userLastName: getFirstValue(userMaster, [
    'LastName',
    'lastName',
    'UserLastName',
    'userLastName'
  ])
});

const appendSearchParam = (searchParams, key, value) => {
  if (value !== undefined && value !== null && String(value).trim()) {
    searchParams.set(key, String(value).trim());
  }
};

const buildWelcomeTitle = conversationUser => {
  const firstName = conversationUser.userFirstName;

  return firstName
    ? `Welcome ${firstName} to NotaryGPT`
    : 'Welcome to NotaryGPT';
};

const buildPortalUrl = (
  url,
  theme,
  embedded,
  hideBackButton,
  showTelemetry,
  conversationUser
) => {
  if (!url) {
    return '';
  }

  const normalizedTheme = theme === 'dark' ? 'dark' : 'light';
  const normalizedEmbedded = embedded ? 'true' : 'false';
  const normalizedHideBackButton = hideBackButton ? 'true' : 'false';
  const normalizedShowTelemetry = showTelemetry ? 'true' : 'false';
  const identityParams = resolveConversationUser(conversationUser);
  const title = buildWelcomeTitle(identityParams);

  try {
    const baseUrl =
      typeof window !== 'undefined' ? window.location.origin : undefined;
    const portalUrl = new URL(url, baseUrl);
    portalUrl.searchParams.set('theme', normalizedTheme);
    portalUrl.searchParams.set('embedded', normalizedEmbedded);
    portalUrl.searchParams.set('hideBackButton', normalizedHideBackButton);
    portalUrl.searchParams.set('hideBack', normalizedHideBackButton);
    portalUrl.searchParams.set(
      'showBackButton',
      hideBackButton ? 'false' : 'true'
    );
    portalUrl.searchParams.set('showTelemetry', normalizedShowTelemetry);
    portalUrl.searchParams.set('title', title);
    portalUrl.searchParams.set('subtext', DEFAULT_EMBED_SUBTEXT);
    Object.entries(identityParams).forEach(([key, value]) =>
      appendSearchParam(portalUrl.searchParams, key, value)
    );
    return portalUrl.toString();
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    const params = new URLSearchParams();
    params.set('theme', normalizedTheme);
    params.set('embedded', normalizedEmbedded);
    params.set('hideBackButton', normalizedHideBackButton);
    params.set('hideBack', normalizedHideBackButton);
    params.set('showBackButton', hideBackButton ? 'false' : 'true');
    params.set('showTelemetry', normalizedShowTelemetry);
    params.set('title', title);
    params.set('subtext', DEFAULT_EMBED_SUBTEXT);
    Object.entries(identityParams).forEach(([key, value]) =>
      appendSearchParam(params, key, value)
    );
    return `${url}${separator}${params.toString()}`;
  }
};

const NotaryGptPage = ({
  enabled = config.notaryGptEnabled,
  portalUrl = config.notaryGptPortalUrl,
  dashboardTheme,
  embedded = true,
  hideBackButton = true,
  showTelemetry = false
}) => {
  const appContext = useContext(AppContext);
  const userMaster = useSelector(state => state.app.userMaster);
  const isDark = appContext?.config?.isDark || appContext?.isDark || false;
  const [loadState, setLoadState] = useState('loading');
  const [retryKey, setRetryKey] = useState(0);
  const resolvedDashboardTheme = dashboardTheme || (isDark ? 'dark' : 'light');
  const normalizedPortalUrl = useMemo(
    () => normalizePortalUrl(portalUrl),
    [portalUrl]
  );
  const iframePortalUrl = useMemo(
    () =>
      buildPortalUrl(
        normalizedPortalUrl,
        resolvedDashboardTheme,
        embedded,
        hideBackButton,
        showTelemetry,
        userMaster
      ),
    [
      embedded,
      hideBackButton,
      normalizedPortalUrl,
      resolvedDashboardTheme,
      showTelemetry,
      userMaster
    ]
  );

  useEffect(() => {
    if (!enabled || !normalizedPortalUrl) {
      setLoadState('unavailable');
      return undefined;
    }

    setLoadState('loading');
    logger.log('NotaryGPT iframe page loading', {
      source: 'NotaryGptPage',
      portalUrl: iframePortalUrl
    });

    const timeout = window.setTimeout(() => {
      setLoadState(currentState =>
        currentState === 'loading' ? 'timeout' : currentState
      );
      logger.warn('NotaryGPT iframe load timeout', {
        source: 'NotaryGptPage',
        portalUrl: iframePortalUrl
      });
    }, LOAD_TIMEOUT_MS);

    return () => window.clearTimeout(timeout);
  }, [enabled, iframePortalUrl, normalizedPortalUrl, retryKey]);

  const handleLoad = () => {
    setLoadState('loaded');
    logger.log('NotaryGPT iframe loaded', {
      source: 'NotaryGptPage',
      portalUrl: iframePortalUrl
    });
  };

  const handleRetry = () => {
    setRetryKey(key => key + 1);
  };

  if (!enabled || !normalizedPortalUrl) {
    return (
      <Alert variant="warning" className="mb-0">
        <Alert.Heading>NotaryGPT is not configured</Alert.Heading>
        <p className="mb-0">
          NotaryGPT is disabled or missing a portal URL for this environment.
        </p>
      </Alert>
    );
  }

  return (
    <section className="notarygpt-page" aria-label="NotaryGPT">
      <div className="notarygpt-page__shell">
        <iframe
          key={retryKey}
          title="NotaryGPT"
          src={iframePortalUrl}
          className="notarygpt-page__iframe"
          allow="clipboard-write"
          onLoad={handleLoad}
        />

        {loadState === 'loading' && (
          <div className="notarygpt-page__overlay" role="status">
            <Spinner animation="border" />
            <span>Loading NotaryGPT...</span>
          </div>
        )}

        {loadState === 'timeout' && (
          <div className="notarygpt-page__overlay" role="alert">
            <h2 className="h5 mb-0">
              NotaryGPT is taking longer than expected
            </h2>
            <p className="mb-0">
              The embedded assistant has not finished loading. You can retry
              loading it.
            </p>
            <div className="notarygpt-page__actions">
              <Button variant="primary" size="sm" onClick={handleRetry}>
                Retry
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NotaryGptPage;
